import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule} from '@angular/forms';
import {BaseCustomInputComponent} from '../base-custom-input.component';
import {NgClass, NgIf} from '@angular/common';
import {MatTooltipModule} from '@angular/material/tooltip';
import {AutoSelectInputDirective} from '../../directives/auto-select-input.directive';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'app-text-input',
  standalone: true,
  styles: [`
    input {
      outline: none;
      border: 1px solid rgb(118, 118, 118);
      border-radius: 4px;
      background-color: white;
      color: black;
      height: 23px;
      padding: 2px 4px;
      font-size: inherit;
      font-family: inherit;
      box-sizing: border-box;
    }
    /* MAP-1533: the input flex-grows to fill its column instead of relying on
       a fixed w-100, so ng-content (e.g. a trailing "find image" button) can
       sit inside the SAME column alongside it without a wrapping row/flex
       container of its own — that wrapper was what threw off left-alignment
       versus sibling fields. */
    .app-text-input {
      flex: 1 1 auto;
      min-width: 0;
    }
    .hidden-div {
      width: auto;
      display: inline-block;
      visibility: hidden;
      position: fixed;
      overflow: auto;
      font-size: 18px;
      padding: 6px;
      white-space: nowrap;
      font-family: inherit;
    }
  `],
  template: `
    <div class="row align-items-center no-gutter" #inputWrapper [ngClass]="{'mt-2': !noTopMargin}">
      <div class="col-4 pe-1" *ngIf="label && !isIcon">
        {{label}}
      </div>
      <div class="col-2 justify-content-center align-items-center d-flex"
           *ngIf="label && isIcon"
           [matTooltip]="tooltip || placeholder">
        <span class="material-symbols-rounded"><label>{{label}}</label></span>
      </div>
      <div class="d-flex align-items-center" [ngClass]="{
        'col-8': label && !isIcon,
        'col-10': label && isIcon,
        'col-12': !label,
        'p-0': autoAdjust
      }">

        @if (!disableFormControl) {
          <input
            #actualInput
            class="app-text-input"
            [type]="type"
            autoSelect
            [placeholder]="placeholder"
            [autoSelectEnabled]="autoFocusAndSelect"
            [formControl]="formControl"
            [value]="value"
            (blur)="hasBlured.emit($event)"
            (input)="onUserInput()"
            (keydown)="handleKeydown($event)"
            (paste)="handlePaste($event)"
          />
        } @else {
          <input
            #actualInput
            class="app-text-input"
            [type]="type"
            autoSelect
            [placeholder]="placeholder"
            [autoSelectEnabled]="autoFocusAndSelect"
            [value]="value"
            (blur)="hasBlured.emit($event)"
            (input)="onUserInput()"
            (keydown)="handleKeydown($event)"
            (paste)="handlePaste($event)"
          />
        }
        <ng-content></ng-content>
      </div>
    </div>
    <div *ngIf="autoAdjust" class="hidden-div" #hiddenDiv>
      {{value}}
    </div>
  `,
  imports: [FormsModule, ReactiveFormsModule, NgClass, NgIf, MatTooltipModule, AutoSelectInputDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextInputComponent),
      multi: true,
    },
  ],
})
export class TextInputComponent extends BaseCustomInputComponent implements AfterViewInit, OnChanges {
  @Input() label = '';
  @Input() placeholder = '';
  @Input() tooltip = '';
  @Input() value: string | number | undefined = '';
  @Input() isIcon = false;
  @Input() noTopMargin = false;
  @Input() autoFocusAndSelect = false;
  @Input() autoAdjust = false;
  @Input() autoSelect = false;
  @Input() minWidth = 0;
  @Input() maxWidth = Infinity;
  @Input() disableFormControl = false;

  @Input() pasteOnly = false;
  @Input() type: 'text' | 'password' = 'text';

  @Output() hasBlured = new EventEmitter<any>();

  //@ts-ignore
  @ViewChild('inputWrapper', { static: false }) inputWrapper: ElementRef;
  //@ts-ignore
  @ViewChild('hiddenDiv', { static: false }) hiddenDiv: ElementRef;
  //@ts-ignore
  @ViewChild('actualInput', { static: false }) actualInput: ElementRef;

  private isUserTyping = false;
  private typingTimeout: any;

  ngAfterViewInit(): void {
    if (this.autoAdjust) {
      this.adjustInputWidth();
    }
    if (this.autoSelect) {
      //@ts-ignore
      const input = this.inputWrapper.nativeElement.querySelector('input');
      input.select();
    }
  }

  focus(): void {
    this.actualInput?.nativeElement?.focus();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && this.autoAdjust && this.hiddenDiv && !this.isUserTyping) {
      setTimeout(() => {
        if (!this.isUserTyping) this.adjustInputWidth();
      }, 50);
    }
  }

  override writeValue(value: string): void {
    super.writeValue(value);
    if (this.autoAdjust && this.hiddenDiv && !this.isUserTyping) {
      setTimeout(() => {
        if (!this.isUserTyping) this.adjustInputWidth();
      }, 50);
    }
  }

  onUserInput() {
    this.isUserTyping = true;
    if (this.typingTimeout) clearTimeout(this.typingTimeout);

    this.adjustInputWidth();

    this.typingTimeout = setTimeout(() => {
      this.isUserTyping = false;
    }, 300);
  }

  handleKeydown(e: KeyboardEvent) {
    if (!this.pasteOnly) return;

    const key = e.key;

    const isPasteCombo =
      (e.ctrlKey || e.metaKey) && (key === 'v' || key === 'V');

    if (key === 'Backspace' || key === 'Delete') {
      e.preventDefault(); // avoid default per-char delete
      this.clearInput();
      return;
    }

    if (!isPasteCombo) {
      e.preventDefault();
    }
  }

  handlePaste(event: ClipboardEvent) {
    if (!this.pasteOnly) return;

    event.preventDefault();
    let text = event.clipboardData?.getData('text') ?? '';

    // Trim whitespace and remove any hidden characters
    text = text.trim().replace(/[\u200B-\u200D\uFEFF]/g, '');

    // For password/API key inputs, sanitize the input
    if (this.type === 'password') {
      // Remove any non-standard characters that might distort the key
      text = text.replace(/[^a-zA-Z0-9_\-\.]/g, '');
    }

    this.formControl?.setValue(text);
    this.value = text;

    setTimeout(() => this.adjustInputWidth(), 0);
  }

  private clearInput() {
    this.formControl?.setValue('');
    this.value = '';
    setTimeout(() => this.adjustInputWidth(), 0);
  }

  adjustInputWidth() {
    if (!this.autoAdjust || !this.hiddenDiv || !this.actualInput) return;

    const currentValue = this.actualInput.nativeElement.value || this.value || '';
    const hiddenDiv = this.hiddenDiv.nativeElement as HTMLInputElement;
    hiddenDiv.innerText = currentValue as string;

    const currentWidth = this.inputWrapper.nativeElement.offsetWidth;
    const neededWidth = hiddenDiv.clientWidth;

    if (neededWidth > currentWidth || neededWidth < currentWidth - 20) {
      const constrainedWidth = Math.max(this.minWidth, Math.min(this.maxWidth, neededWidth));
      this.inputWrapper.nativeElement.style.width = constrainedWidth + 'px';
    }
  }
}
